/**
 * The design's 4-digit PIN before Text and the Vault - TEST VERSION, kept on this device.
 * There is no server PIN yet (PLAN.md §5). Here the fan creates a PIN the first time (typed twice); only a SHA-256 hash
 * (salted with the fan's id) is kept in this browser. "Forgot?" = sign out and back in with an email code (that clears
 * it). 3 wrong tries -> 30 s wait. The unlock lasts 12 hours on this device (owner 11-04: reload/restart must not ask again); signing out ends it.
 * Demo: the demo member's PIN is preset to 1234 so reviewers can get in.
 * Moving it to the server later = replace hashPin/stored/save below with API calls; the dialog stays the same.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { FEATURES, IS_MOCK } from '~/config/mode'
import { signOutEverywhere } from './session'
import { Icon, useShowModal } from './ui'
import { pinApi } from '~/api/endpoints'

const LEN = 4, MAX_TRIES = 3, LOCK_SECONDS = 30
const UNLOCK_KEY = 'cw-pin-unlocked'
const pinKey = (uid: string) => `cw-pin:${uid}`
const safe = <T,>(f: () => T, fallback: T) => { try { return f() } catch { return fallback } }

async function hashPin(uid: string, pin: string) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`caryns-world:${uid}:${pin}`))
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
}
export const hasPin = (uid: string) => !!safe(() => localStorage.getItem(pinKey(uid)), null)
export const clearPin = (uid: string) => safe(() => { localStorage.removeItem(pinKey(uid)); localStorage.removeItem(UNLOCK_KEY) }, undefined)
const UNLOCK_HOURS = 12
const unlocked = () => safe(() => Number(localStorage.getItem(UNLOCK_KEY) || 0) > Date.now(), false)
export const endPinUnlock = () => safe(() => localStorage.removeItem(UNLOCK_KEY), undefined)

type Target = string
type Mode = 'enter' | 'create' | 'confirm' | 'current' | 'loading'
const Ctx = createContext<{ go: (to: Target) => void; isUnlocked: () => boolean; ask: (to: Target) => void; changePin: () => void }>({ go: () => {}, isUnlocked: () => true, ask: () => {}, changePin: () => {} })
export const usePin = () => useContext(Ctx)

export function PinProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const uid = user?.id || ''
  const [target, setTarget] = useState<Target | null>(null)
  const [changing, setChanging] = useState(false)

  // Demo: preset the demo member's PIN to 1234 (once)
  useEffect(() => { if (IS_MOCK && uid && !hasPin(uid)) hashPin(uid, '1234').then((h) => safe(() => localStorage.setItem(pinKey(uid), h), undefined)) }, [uid])

  const isUnlocked = useCallback(() => !FEATURES.pinGate || unlocked(), [])
  const go = useCallback((to: Target) => { if (isUnlocked()) navigate(to); else setTarget(to) }, [isUnlocked, navigate])
  return (
    <Ctx.Provider value={{ go, isUnlocked, ask: setTarget, changePin: () => setChanging(true) }}>
      {children}
      {(target || changing) && uid && (
        <PinDialog uid={uid} target={target} change={changing}
          onClose={() => { setTarget(null); setChanging(false) }}
          onUnlocked={() => { const t = target; setTarget(null); setChanging(false); if (t) navigate(t) }} />
      )}
    </Ctx.Provider>
  )
}

function PinDialog({ uid, target, change, onClose, onUnlocked }: { uid: string; target: Target | null; change: boolean; onClose: () => void; onUnlocked: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const navigate = useNavigate()
  // owner 11-04: the PIN is saved ON THE ACCOUNT (Caryn's server) - set it once, then every browser/phone asks to ENTER it.
  // server: true/false = the account has / has no PIN; null = this server has no PIN routes yet -> this device only (as before)
  const [mode, setMode] = useState<Mode>('loading')
  const [server, setServer] = useState<boolean | null>(null)
  const current = useRef('')
  useEffect(() => {
    let alive = true
    ;(async () => {
      const has = await pinApi.status().catch(() => null)
      if (!alive) return
      const forgotKey = `cw-pin-forgot:${uid}`
      if (has && safe(() => localStorage.getItem(forgotKey), null)) { // signed back in after "Forgot?" -> choose a new PIN
        const ok = await pinApi.reset().catch(() => false)
        safe(() => localStorage.removeItem(forgotKey), undefined)
        if (ok && alive) { setServer(false); setMode('create'); return }
      }
      if (!alive) return
      setServer(has)
      if (change) setMode(has ? 'current' : 'create')
      else setMode(has || hasPin(uid) ? 'enter' : 'create')
    })()
    return () => { alive = false }
  }, [uid, change])
  const [first, setFirst] = useState('')
  const [entry, setEntry] = useState('')
  const [msg, setMsg] = useState<{ t: string; err?: boolean }>({ t: '' })
  const [state, setState] = useState<'' | 'shake' | 'ok'>('')
  const [tries, setTries] = useState(0)
  const [lockedUntil, setLockedUntil] = useState(0)
  const [, tick] = useState(0)
  const busy = useRef(false)
  const [confirmForgot, setConfirmForgot] = useState(false)

  useShowModal(ref)
  useEffect(() => {
    if (!lockedUntil) return
    const t = setInterval(() => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000)
      if (left <= 0) { setLockedUntil(0); setTries(0); setMsg({ t: '' }) } else { setMsg({ t: `Too many tries. Please wait ${left}s.`, err: true }); tick((n) => n + 1) }
    }, 250)
    return () => clearInterval(t)
  }, [lockedUntil])

  const locked = Date.now() < lockedUntil
  const reset = (after = 450) => setTimeout(() => { setEntry(''); setState(''); busy.current = false }, after)
  const done = () => {
    setState('ok'); setMsg({ t: change ? 'PIN saved' : 'Unlocked' })
    safe(() => localStorage.setItem(UNLOCK_KEY, String(Date.now() + UNLOCK_HOURS * 3600e3)), undefined)
    setTimeout(onUnlocked, 450)
  }

  const saveLocal = async (code: string) => { const h = await hashPin(uid, code); safe(() => localStorage.setItem(pinKey(uid), h), undefined) }
  const wrong = (r?: { ok: boolean; triesLeft?: number; locked?: boolean } | null) => {
    setState('shake')
    setTimeout(() => {
      setEntry(''); setState(''); busy.current = false
      if (r?.locked) { setLockedUntil(Date.now() + 60 * 1000); return }
      if (r && typeof r.triesLeft === 'number') { setMsg({ t: `That PIN isn’t right. ${r.triesLeft} ${r.triesLeft === 1 ? 'try' : 'tries'} left.`, err: true }); return }
      const n = tries + 1
      setTries(n)
      if (n >= MAX_TRIES) setLockedUntil(Date.now() + LOCK_SECONDS * 1000)
      else setMsg({ t: `That PIN isn’t right. ${MAX_TRIES - n} ${MAX_TRIES - n === 1 ? 'try' : 'tries'} left.`, err: true })
    }, 450)
  }

  async function full(code: string) {
    busy.current = true
    if (mode === 'loading') { setEntry(''); busy.current = false; return }
    if (mode === 'current') { // changing it: the PIN used now first
      const r = await pinApi.verify(code)
      if (r?.ok) { current.current = code; setMode('create'); setMsg({ t: '' }); setEntry(''); busy.current = false; return }
      return wrong(r)
    }
    // straight to "Type it once more" - no pause, so a fast typist's next digits are not lost
    if (mode === 'create') { setFirst(code); setMode('confirm'); setMsg({ t: '' }); setEntry(''); busy.current = false; return }
    if (mode === 'confirm') {
      if (code !== first) { setState('shake'); setMsg({ t: 'Those didn’t match. Choose your PIN again.', err: true }); setMode('create'); setFirst(''); reset(); return }
      if (server !== null && !(await pinApi.set(code, current.current || undefined))) {
        setState('shake'); setMsg({ t: 'Couldn’t save your PIN. Please try again.', err: true }); setMode('create'); setFirst(''); reset(); return
      }
      await saveLocal(code)
      return done()
    }
    // enter
    if (server === true) {
      const r = await pinApi.verify(code)
      if (r?.ok) { await saveLocal(code); return done() }
      if (r) return wrong(r)
      // the server could not answer: fall back to this device's copy
    }
    const ok = (await hashPin(uid, code)) === safe(() => localStorage.getItem(pinKey(uid)), null)
    if (ok) {
      if (server === false) pinApi.set(code).catch(() => {}) // a PIN set before on this device moves to the account
      return done()
    }
    wrong(null)
  }
  const press = (d: string) => {
    if (busy.current || locked || entry.length >= LEN) return
    const next = entry + d
    setEntry(next); setMsg({ t: '' })
    if (next.length === LEN) full(next)
  }
  const back = () => { if (!busy.current) setEntry((e) => e.slice(0, -1)) }
  const forgot = async () => {
    if (mode !== 'enter') return
    if (!confirmForgot) { setConfirmForgot(true); setMsg({ t: 'Tap “Forgot?” again to sign out. Sign back in with your email code to choose a new PIN.' }); return }
    clearPin(uid)
    safe(() => localStorage.setItem(`cw-pin-forgot:${uid}`, '1'), undefined) // after signing back in: choose a new PIN
    await signOutEverywhere(navigate)
  }

  const title = mode === 'enter' || mode === 'loading' ? 'Enter your PIN' : mode === 'current' ? 'Enter your current PIN' : mode === 'create' ? (change ? 'Choose a new PIN' : 'Create your PIN') : 'Type it once more'
  const sub = mode === 'loading' ? 'One moment…' : mode === 'current' ? 'To change it, enter the PIN you use now.' : mode === 'enter'
    ? (target === '/vault' ? 'Your vault is private. Enter your 4-digit PIN to continue.' : 'Your texts are private. Enter your 4-digit PIN to continue.')
    : mode === 'create' ? 'Pick 4 digits to keep your texts and vault private on this device.' : 'Confirm your new 4-digit PIN.'

  return (
    <dialog className="pin on-dark" ref={ref} aria-labelledby="pinTitle" onClose={onClose}
      onClick={(e) => { if (e.target === ref.current) ref.current?.close() }}
      onKeyDown={(e) => { if (/^\d$/.test(e.key)) { e.preventDefault(); press(e.key) } else if (e.key === 'Backspace') back() }}>
      <button className="icon-btn pin-close" type="button" aria-label="Cancel" onClick={() => ref.current?.close()}><Icon id="i-close" /></button>
      <div className="pin-lock" aria-hidden="true"><Icon id="i-lock" /></div>
      <h2 id="pinTitle">{title}</h2>
      <p className="pin-sub">{sub}</p>
      <div className={`pin-dots${state ? ' ' + state : ''}`} aria-hidden="true">
        {Array.from({ length: LEN }, (_, i) => <span key={i} className={`pin-dot${i < entry.length ? ' on' : ''}`} />)}
      </div>
      <p className={`pin-msg${msg.err ? ' err' : ''}`} role="status" aria-live="polite">{msg.t}</p>
      <div className="keypad" role="group" aria-label="PIN keypad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => <button key={k} className="key" type="button" disabled={locked || mode === 'loading'} onClick={() => press(k)}>{k}</button>)}
        <button className="key fn pin-forgot" type="button" onClick={forgot} style={mode === 'enter' ? undefined : { visibility: 'hidden' }}>Forgot?</button>
        <button className="key" type="button" disabled={locked || mode === 'loading'} onClick={() => press('0')}>0</button>
        <button className="key fn" type="button" aria-label="Delete last digit" disabled={locked} onClick={back}><Icon id="i-send" style={{ transform: 'scaleX(-1)' }} /></button>
      </div>
      <p className="pin-sub" style={{ marginTop: 14, fontSize: '.8125rem', opacity: 0.8 }}>
        {IS_MOCK && mode === 'enter' ? 'Demo PIN: 1234' : server === null ? 'Your PIN is kept on this device.' : 'Your PIN is saved to your account - it works on every device.'}
      </p>
    </dialog>
  )
}
